import { ArrowLeftOutlined } from "@ant-design/icons";
import { Breadcrumb, Button, Card, Empty, Flex, Skeleton, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { Navigate, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../hooks/redux-hooks";
import { type ComplianceTemplateSettings, useGetComplianceSettingsQuery } from "../../service/compliance.service";
import styles from "./compliance.module.scss";

const templateColumns = (
  onEdit: (tmpl: ComplianceTemplateSettings) => void,
): ColumnsType<ComplianceTemplateSettings> => [
  {
    title: "Template",
    dataIndex: "name",
    key: "name",
    sorter: (a, b) => a.name.localeCompare(b.name),
    render: (v: string) => <Typography.Text strong>{v}</Typography.Text>,
  },
  {
    title: "Owner",
    dataIndex: "owner_name",
    key: "owner_name",
    width: 200,
    sorter: (a, b) => (a.owner_name ?? "").localeCompare(b.owner_name ?? ""),
    render: (v: string) =>
      v ? (
        <Typography.Text style={{ fontSize: 13 }}>{v}</Typography.Text>
      ) : (
        <Typography.Text style={{ color: "#9ca3af" }}>—</Typography.Text>
      ),
  },
  {
    title: "Status",
    key: "status",
    width: 160,
    render: (_value, row) =>
      row.instructions?.trim() ? (
        <Tag color="success" style={{ borderRadius: 999 }}>
          Configured
        </Tag>
      ) : (
        <Tag style={{ borderRadius: 999 }}>No rules yet</Tag>
      ),
  },
  {
    title: "",
    key: "actions",
    width: 140,
    render: (_value, row) => (
      <Button
        type="link"
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onEdit(row);
        }}
      >
        {row.instructions?.trim() ? "Edit rules →" : "Add rules →"}
      </Button>
    ),
  },
];

const ComplianceManageRules = () => {
  const navigate = useNavigate();
  const { userInfo } = useAppSelector((state) => state.auth);
  const { data, isLoading } = useGetComplianceSettingsQuery();

  if (!userInfo?.show_compliance_dashboard || !userInfo?.is_group_admin) {
    return <Navigate to="/home" replace />;
  }

  const templates = data?.templates ?? [];

  return (
    <div className={styles.compliance__container}>
      <Flex align="center" gap={8} wrap="wrap" style={{ marginBottom: 16 }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/compliance")}
          size="small"
          style={{ color: "#6b7280" }}
        />
        <Breadcrumb
          items={[
            {
              title: (
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/compliance");
                  }}
                >
                  Compliance Dashboard
                </a>
              ),
            },
            { title: "Manage Rules" },
          ]}
        />
      </Flex>

      <Flex vertical gap={4} style={{ marginBottom: 20 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          Manage Rules
        </Typography.Title>
        <Typography.Text style={{ color: "#6b7280" }}>
          Compliance rules are configured per group-shared note template. Click a row to add or edit rules for that
          template.
        </Typography.Text>
      </Flex>

      {isLoading ? (
        <Skeleton active title={{ width: "30%" }} paragraph={{ rows: 6 }} style={{ padding: "8px 0" }} />
      ) : templates.length === 0 ? (
        <Empty
          description={
            <Flex vertical align="center" gap={8}>
              <Typography.Text>No group-shared templates found.</Typography.Text>
              <Typography.Text style={{ color: "#9ca3af", fontSize: 12 }}>
                Share a custom note template with your group from the Templates page, then return here to configure
                compliance rules.
              </Typography.Text>
            </Flex>
          }
          style={{ padding: 32 }}
        />
      ) : (
        <Table
          columns={templateColumns((tmpl) => navigate(`/compliance/manage-rules/${tmpl.id}`))}
          dataSource={templates}
          rowKey="id"
          pagination={{ pageSize: 20, hideOnSinglePage: true }}
          onRow={(row) => ({
            onClick: () => navigate(`/compliance/manage-rules/${row.id}`),
            style: { cursor: "pointer" },
          })}
          size="middle"
        />
      )}
    </div>
  );
};

export default ComplianceManageRules;
